import { AI_USAGE_FEATURE_META, AI_USAGE_FEATURE_ORDER } from './ai.usage.features';

/** Socket/CQRS action names — must match class names registered in tenant/ai/usage. */
export enum AIUsageAction {
  GetBalances = 'GetAIUsageBalancesQuery',
  GetAnalytics = 'GetAIUsageAnalyticsQuery',
  GetLimitStatus = 'GetAIUsageLimitStatusQuery',
}

/** @deprecated Prefer AIUsageAction */
export const UsageCreditsAction = AIUsageAction;

export type UsageCreditsPeriodMode = 'week' | 'month';
export type AIUsagePeriodMode = UsageCreditsPeriodMode;

export interface UsageCreditsFeatureBalance {
  id: string;
  name: string;
  description: string;
  balance: number;
  icon: string;
}

export interface UsageCreditsBalances {
  workspaceBalance: number;
  featureCredits: UsageCreditsFeatureBalance[];
}

export type AIUsageBalances = UsageCreditsBalances;

export interface AIUsageAnalyticsRequest {
  periodMode: UsageCreditsPeriodMode;
  startDate: string;
  endDate: string;
  /** Optional filter: namespaced userId (tenant::env::contactId) or bare contact id */
  userId?: string | null;
}

/** @deprecated Prefer AIUsageAnalyticsRequest */
export type UsageCreditsAnalyticsRequest = AIUsageAnalyticsRequest;

export interface AIUsageDailyPoint {
  date: string;
  label: string;
  /** Est. cost USD (sum of featureCosts) */
  cost: number;
  requests: number;
  agentRequests: number;
  workflowRequests: number;
  inputTokens: number;
  outputTokens: number;
  /**
   * Est. cost by normalized feature/resource id for this day.
   * Keys match feature usage rows (e.g. workbuddy-agent, task-submission-validation).
   */
  featureCosts: Record<string, number>;
}

/** @deprecated Prefer AIUsageDailyPoint */
export type UsageCreditsSpendPoint = AIUsageDailyPoint;

/** Who the usage principal is — contacts or OAuth apps both log under tenant::env::id. */
export type AIUsagePrincipalType = 'contact' | 'oauth_application' | 'unknown';

export interface AIUsageUserSummary {
  /** Namespaced userId as stored on AIDailyUsage */
  userId: string;
  /** Bare id (contact or OAuth application) when parseable */
  contactId: string;
  userName: string;
  email?: string;
  principalType: AIUsagePrincipalType;
  totalRequests: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
}

export interface AIUsageResourceBreakdown {
  resourceId: string;
  name: string;
  messageCount: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  cost: number;
  unpriced: boolean;
}

/** @deprecated Prefer AIUsageResourceBreakdown */
export interface UsageCreditsFeatureUsage {
  id: string;
  name: string;
  amount: number;
  color: string;
}

/** @deprecated Prefer AIUsageUserSummary for user rows */
export interface UsageCreditsSession {
  id: string;
  title: string;
  startedAt: string;
  amount: number;
}

export interface AIUsageTotals {
  requests: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}

export interface AIUsageAnalytics {
  periodMode: UsageCreditsPeriodMode;
  startDate: string;
  endDate: string;
  selectedUserId: string | null;
  dailySeries: AIUsageDailyPoint[];
  usersWithUsage: AIUsageUserSummary[];
  resourceBreakdown: AIUsageResourceBreakdown[];
  totals: AIUsageTotals;
  /** Back-compat aliases for earlier UI */
  spendSeries: AIUsageDailyPoint[];
  featureUsage: UsageCreditsFeatureUsage[];
  sessions: UsageCreditsSession[];
  totalSpend: number;
}

/** @deprecated Prefer AIUsageAnalytics */
export type UsageCreditsAnalytics = AIUsageAnalytics;

/*
 * Feature catalog and run-id resolution live in ./ai.usage.features — shared with the web so
 * the admin run-id view and the API grouping can never disagree about what a run id means.
 */

/**
 * Swatch colours keyed by feature id. Derived from AI_USAGE_FEATURE_META so a feature's
 * colour is fixed rather than assigned by row position — it stays the same across periods
 * even as the usage ordering changes.
 */
export const USAGE_CREDITS_FEATURE_COLORS: Record<string, string> = {
  ...Object.fromEntries(Object.values(AI_USAGE_FEATURE_META).map(meta => [meta.id, meta.color])),
  default: '#64748B',
};

export const AI_USAGE_FEATURE_COLORS = USAGE_CREDITS_FEATURE_COLORS;

/**
 * Default balances when no billing ledger exists yet (read-only v1).
 * Balances stay 0 until purchase / allocation APIs exist.
 */
export const DEFAULT_USAGE_CREDITS_BALANCES: UsageCreditsBalances = {
  workspaceBalance: 0,
  // Same feature ids the usage table groups by, so allocations line up with reported spend.
  featureCredits: AI_USAGE_FEATURE_ORDER.map(id => ({
    id,
    name: AI_USAGE_FEATURE_META[id].name,
    description: 'Included feature credit allocation',
    balance: 0,
    icon: AI_USAGE_FEATURE_META[id].icon,
  })),
};

export const DEFAULT_AI_USAGE_BALANCES = DEFAULT_USAGE_CREDITS_BALANCES;
